<template>
  <vx-card class="mt-base">
    <h4 class="mb-5">
      <strong>1. Density</strong> is how much hair you have. This is about the quantity of hair on
      your head, and it can range from low density to medium density to high density.
    </h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/hairdressing/activity_11.png')"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt="Three circles of dots showing hair density: low density has a few widely spaced dots, medium density has more dots, and high density is tightly packed with dots."
    />
    <h4 class="mb-5">Take the Density Test: What Is the Density of My Hair?</h4>
    <p class="mb-5">
      Density refers to how many strands of hair are packed into the space on your scalp. The most
      accurate way to calculate your hair’s density is to count them - the average person has 2,200
      strands per square inch! This could get slightly time-consuming!
    </p>
    <p class="mb-5">
      Luckily, there are two methods to quickly assess whether your hair density is low, medium, or
      high. Let’s take a look:
    </p>
    <p class="mb-5">
      <i><strong>i. The Scalp Test</strong></i>
    </p>
    <p class="mb-5">Make sure your hair is dry and let it hang loose - don’t part it!</p>
    <p class="mb-5">Now, using a mirror, take a close look at your hair and head.</p>

    <ul class="default-list">
      <li>Can you see your scalp easily? If yes, then you have low hair density.</li>
      <li>
        Can you see some of your scalp but not too easily? If yes, then your hair is medium density.
      </li>
      <li>Is your scalp difficult to see? If yes, then you have high hair density.</li>
    </ul>

    <centered-image-wrapper
      :image="require('@/assets/images/experiences/hairdressing/activity_12.png')"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt=""
    />

    <p class="mb-5">
      <i><strong>ii. The Ponytail Test</strong></i>
    </p>
    <ul class="default-list">
      <li>
        If you have long hair, gather it up into a ponytail and, using a tailor’s tape, measure the
        circumference of your ponytail.
      </li>
      <li>If your ponytail circumference is less than 5cm, then your hair density is low.</li>
      <li>
        If your ponytail circumference is between 5 and 10cm, then you have medium hair density.
      </li>
      <li>If your ponytail circumference is at least 10cm, then you have high hair density.</li>
    </ul>
    <div v-for="(question, index) in questions" :key="index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <div class="vx-row my-5">
        <div class="vx-col w-full mb-2" v-if="isEditable">
          <select
            class="default-select"
            v-model="answers['question' + index]"
            :aria-label="question.question"
          >
            <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
              {{ answer.answer }}
            </option>
          </select>
        </div>
        <div class="my-2 mx-5" v-else>
          <p
            class="my-2"
            v-for="answer in question.answers"
            :key="answer.val"
            v-if="answers['question' + index] != 0"
          >
            <span v-if="answers['question' + index] == answer.val">{{ answer.answer }}</span>
          </p>
          <p class="my-2" v-else>N/A</p>
        </div>
      </div>
    </div>

    <h4 class="mb-5">Density: What Does This Mean for Hair Treatment?</h4>

    <p class="mb-5">For low-density hair:</p>

    <ul class="default-list">
      <li>Use volumising shampoo</li>
      <li>Add waves and curls to create body</li>
      <li>Blow-dry and flick out ends</li>
      <li>Use short styles</li>
      <li>Cut a fringe</li>
    </ul>
    <h4 class="mb-5">For high-density hair:</h4>
    <ul class="default-list">
      <li>Use a shampoo formulated to give a thinner look</li>
      <li>Use a flat iron</li>
      <li>Wash hair less often</li>
      <li>Dry gently with a towel</li>
      <li>Let hair dry 75% before blow-drying.</li>
    </ul>
    <save-buttons
      :is-editable="isEditable"
      @save="save"
      :student-back="
        '/student/work-experience/' + this.$route.params.id + '/activities/identifying-hair-types'
      "
      :teacher-back="
        '/teacher/vje/experience/view/' +
        this.$route.params.id +
        '/activities/identifying-hair-types'
      "
    />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      questions: {
        1: {
          question: 'My hair density is:',
          answers: [
            { val: 0, answer: 'Select your hair density' },
            { val: 1, answer: 'Low Density' },
            { val: 2, answer: 'Medium Density' },
            { val: 3, answer: 'High Density' },
          ],
        },
      },
      answers: {
        question1: 0,
      },
    };
  },
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (this.data.question1) {
      this.answers = this.data;
    }
  },
};
</script>
